import React, { useState, useEffect, useCallback } from 'react';
import {
    Page,
    Layout,
    Card,
    BlockStack,
    InlineStack,
    Text,
    Button,
    Spinner,
    Banner,
    Badge,
    DataTable,
    Select,
    Divider
} from '@shopify/polaris';
import { taxService } from '../services/taxService';
import { accountingHelpers, TAX_RATES } from '../constants/accountingConstants';
import KPICard from '../components/accounting/KPICard';

const TaxesPage = () => {
    // State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [vatReport, setVatReport] = useState(null);
    const [profitTax, setProfitTax] = useState(null);
    const [taxConfig, setTaxConfig] = useState(null);

    // Period selector
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);

    // Load data
    const loadData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const [vatRes, profitRes, configRes] = await Promise.all([
                taxService.getMonthlyVatReport(year, month),
                taxService.getProfitTaxEstimate(year),
                taxService.getConfig()
            ]);

            setVatReport(vatRes.data || null);
            setProfitTax(profitRes.data || null);
            setTaxConfig(configRes.data || null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [year, month]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Render VAT breakdown
    const renderVatBreakdown = () => {
        if (!vatReport?.details) return null;

        const { sales_vat, expense_vat } = vatReport.details;

        return (
            <BlockStack gap="400">
                <Text as="h3" variant="headingSm">Sales VAT (Output)</Text>
                <DataTable
                    columnContentTypes={['text', 'numeric', 'numeric']}
                    headings={['Description', 'Base Amount', 'VAT Amount']}
                    rows={[
                        ['Total Sales', accountingHelpers.formatCurrency(sales_vat?.total_sales || 0), accountingHelpers.formatCurrency(sales_vat?.vat_amount || 0)]
                    ]}
                />

                <Divider />

                <Text as="h3" variant="headingSm">Expense VAT (Input)</Text>
                <DataTable
                    columnContentTypes={['text', 'numeric', 'numeric']}
                    headings={['Description', 'Base Amount', 'VAT Amount']}
                    rows={[
                        ['Total Expenses with VAT', accountingHelpers.formatCurrency(expense_vat?.total_expenses || 0), accountingHelpers.formatCurrency(expense_vat?.vat_amount || 0)]
                    ]}
                />
            </BlockStack>
        );
    };

    // Render profit tax details
    const renderProfitTaxDetails = () => {
        if (!profitTax) return null;

        const { revenue, expenses, profit, tax } = profitTax;

        return (
            <DataTable
                columnContentTypes={['text', 'numeric']}
                headings={['Item', 'Amount']}
                rows={[
                    ['Total Revenue', accountingHelpers.formatCurrency(revenue?.total || 0)],
                    ['Total Expenses', accountingHelpers.formatCurrency(expenses?.total || 0)],
                    ['Net Profit', accountingHelpers.formatCurrency(profit?.net_profit || 0)],
                    ['Taxable Profit', accountingHelpers.formatCurrency(profit?.taxable_profit || 0)],
                    [`Profit Tax (${tax?.tax_rate || TAX_RATES.PROFIT_TAX_STANDARD}%)`, accountingHelpers.formatCurrency(tax?.estimated_tax || 0)]
                ]}
            />
        );
    };

    // Generate year/month options
    const yearOptions = [];
    for (let y = now.getFullYear(); y >= now.getFullYear() - 2; y--) {
        yearOptions.push({ label: String(y), value: String(y) });
    }

    const monthOptions = [
        { label: 'January', value: '1' }, { label: 'February', value: '2' },
        { label: 'March', value: '3' }, { label: 'April', value: '4' },
        { label: 'May', value: '5' }, { label: 'June', value: '6' },
        { label: 'July', value: '7' }, { label: 'August', value: '8' },
        { label: 'September', value: '9' }, { label: 'October', value: '10' },
        { label: 'November', value: '11' }, { label: 'December', value: '12' }
    ];

    if (loading) {
        return (
            <Page title="Tax Management">
                <Layout>
                    <Layout.Section>
                        <Card><InlineStack align="center"><Spinner size="large" /></InlineStack></Card>
                    </Layout.Section>
                </Layout>
            </Page>
        );
    }

    const vatSummary = vatReport?.summary || {};
    const taxSummary = profitTax?.tax || {};

    return (
        <Page
            title="Tax Management"
            subtitle="VAT reports and profit tax estimates for Egypt"
        >
            <Layout>
                {error && (
                    <Layout.Section>
                        <Banner tone="critical" onDismiss={() => setError(null)}>{error}</Banner>
                    </Layout.Section>
                )}

                {/* Period Selector */}
                <Layout.Section>
                    <Card>
                        <InlineStack gap="400" blockAlign="end">
                            <Select label="Year" options={yearOptions} value={String(year)} onChange={(v) => setYear(parseInt(v))} />
                            <Select label="Month" options={monthOptions} value={String(month)} onChange={(v) => setMonth(parseInt(v))} />
                            <Button onClick={loadData}>Load</Button>
                        </InlineStack>
                    </Card>
                </Layout.Section>

                {/* Tax Summary Cards */}
                <Layout.Section>
                    <BlockStack gap="400">
                        <Text as="h2" variant="headingMd">VAT Summary - {monthOptions[month - 1]?.label} {year}</Text>
                        <InlineStack gap="400">
                            <KPICard
                                title="Output VAT (Sales)"
                                value={vatSummary.output_vat || 0}
                                format="currency"
                                subtitle="VAT collected from customers"
                            />
                            <KPICard
                                title="Input VAT (Expenses)"
                                value={vatSummary.input_vat || 0}
                                format="currency"
                                subtitle="VAT paid on purchases"
                            />
                            <KPICard
                                title="Net VAT Payable"
                                value={vatSummary.net_vat_payable || 0}
                                format="currency"
                                subtitle="Amount due to tax authority"
                            />
                        </InlineStack>
                    </BlockStack>
                </Layout.Section>

                {/* VAT Rate Info */}
                <Layout.Section>
                    <Card>
                        <BlockStack gap="300">
                            <InlineStack align="space-between">
                                <Text as="h2" variant="headingMd">Tax Rates (Egypt)</Text>
                            </InlineStack>
                            <InlineStack gap="600">
                                <BlockStack gap="100">
                                    <Text as="p" tone="subdued">VAT Rate</Text>
                                    <Text as="p" variant="headingLg">{taxConfig?.vat?.standard_rate || TAX_RATES.VAT_STANDARD}%</Text>
                                </BlockStack>
                                <BlockStack gap="100">
                                    <Text as="p" tone="subdued">Profit Tax Rate</Text>
                                    <Text as="p" variant="headingLg">{taxConfig?.profit_tax?.rate || TAX_RATES.PROFIT_TAX_STANDARD}%</Text>
                                </BlockStack>
                                <BlockStack gap="100">
                                    <Text as="p" tone="subdued">Small Business Threshold</Text>
                                    <Text as="p" variant="headingLg">{accountingHelpers.formatCurrency(taxConfig?.profit_tax?.small_business_threshold || TAX_RATES.SMALL_BUSINESS_THRESHOLD)}</Text>
                                </BlockStack>
                            </InlineStack>
                        </BlockStack>
                    </Card>
                </Layout.Section>

                {/* VAT Details */}
                <Layout.Section>
                    <Card>
                        <BlockStack gap="400">
                            <Text as="h2" variant="headingMd">VAT Breakdown</Text>
                            {renderVatBreakdown()}
                        </BlockStack>
                    </Card>
                </Layout.Section>

                {/* Profit Tax */}
                <Layout.Section>
                    <Card>
                        <BlockStack gap="400">
                            <InlineStack align="space-between">
                                <Text as="h2" variant="headingMd">Profit Tax Estimate - {year}</Text>
                                {taxSummary.is_small_business && (
                                    <Badge tone="info">Small Business</Badge>
                                )}
                            </InlineStack>
                            <InlineStack gap="400">
                                <KPICard
                                    title="Estimated Annual Tax"
                                    value={taxSummary.estimated_tax || 0}
                                    format="currency"
                                />
                                <KPICard
                                    title="Quarterly Payment"
                                    value={(taxSummary.estimated_tax || 0) / 4}
                                    format="currency"
                                    subtitle="Approximate quarterly installment"
                                />
                            </InlineStack>
                            {renderProfitTaxDetails()}
                        </BlockStack>
                    </Card>
                </Layout.Section>
            </Layout>
        </Page>
    );
};

export default TaxesPage;
